import {Injectable, OnDestroy} from '@angular/core';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {MarkerService} from '@mydocma/tile-viewer';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {MmFaultsHoverService} from '../../../mm-faults-view/services/mm-faults-hover.service';
import {MmFaultsSplitService} from '../../../mm-faults-view/services/mm-faults-split.service';
import {MmPlansManagerService} from '../../services/mm-plans-manager.service';
import {Logger} from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Injectable()
export class MmPlanHoverService implements OnDestroy {

  private baseMarkerService: MarkerService;
  private subscription: Subscription;

  protected takeUntilPlanViewExists = () => <T>(source: Observable<T>) => {
    return source.pipe(
        switchMap(v => this.faultsSplitService.rightViewVisible$.pipe(filter(Boolean), map(m => v)))
    );
  }

  constructor(
    private readonly faultsHoverService: MmFaultsHoverService,
    private readonly plansManagerService: MmPlansManagerService,
    private readonly selectionFaultService: SelectionService<ViewFault>,
    private readonly faultsSplitService: MmFaultsSplitService,
    private readonly logger: Logger
  ) {}

  ngOnDestroy(): void {
  }

  registerHoverService(baseMarkerService: MarkerService): void {
    this.subscription?.unsubscribe();
    this.baseMarkerService = baseMarkerService;

    this.subscription = new Subscription();

    this.subscription.add(this.faultsHoverService.onMouseOverFault.pipe(
      untilDestroyed(this),
      this.takeUntilPlanViewExists(),
      switchMap(fault => this.plansManagerService.selectedPlan$
        .pipe(filter(plan => !!plan?.pins),
          filter(plan => !!fault && !!plan.pins.find(pin => pin.fault.identifier === fault.identifier)),
          map(plan => ({fault, plan})) )
      ),
    ).subscribe({
      next:  (result) => this.baseMarkerService.notifyMarkerOver({id: result.fault.identifier}),
      error: (error) => this.logger.error(error, '', 'MmPlanHoverService')
    }));

    this.subscription.add(this.faultsHoverService.onMouseOutFault.pipe(
      untilDestroyed(this),
      this.takeUntilPlanViewExists(),
      switchMap(fault => this.plansManagerService.selectedPlan$
        .pipe(filter(plan => !!plan?.pins),
          filter(plan => !!fault && !!plan.pins.find(pin => pin.fault.identifier === fault.identifier)),
          map(plan => ({fault, plan})) )
      ),
    ).subscribe({
        next:  (result) => this.baseMarkerService.notifyMarkerOut({id: result.fault.identifier}),
        error: (error) => this.logger.error(error, '', 'MmPlanHoverService')
    }));

    this.subscription.add(this.selectionFaultService.selectedItems.pipe(
        filter(faults => faults?.length > 0),
        switchMap(faults => this.plansManagerService.selectedPlan$.pipe(map(plan => ({faults, plan})))),
        distinctUntilChanged(),
        this.takeUntilPlanViewExists(),
        untilDestroyed(this),
    ).subscribe({
        next: ({faults, plan}) => {
          const faultIds = faults.filter(fault => plan?.pins?.find(pin => pin.fault.identifier === fault.identifier))
                                 .map(fault => ({id: fault.identifier}));

          try {
           this.baseMarkerService.flyToMarkers(faultIds);
          } catch (e) {}
        },
        error: (error) => this.logger.error(error, '', 'MmPlanHoverService')
    }));
  }
}
